import React, { useRef } from 'react';
import styled from 'styled-components';

interface ColorPickerSquareProps {
  color: string;
  onChange: (newColor: string) => void;
}

const HiddenColorInput = styled.input`
  display: none;
`;

const ColorSquare = styled.div<{ color: string }>`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background-color: ${({ color }) => color};
  border: 1px solid #ccc;
  cursor: pointer;
  display: inline-block;
`;

export const ColorPicker: React.FC<ColorPickerSquareProps> = ({ color, onChange }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  return (
    <>
      <ColorSquare color={color} onClick={handleClick} title="Click to change color" />
      <HiddenColorInput
        ref={inputRef}
        type="color"
        value={color}
        onChange={handleChange}
      />
    </>
  );
};

export function generateDistinctColor(existingColors: string[], maxTries = 20): string {
  const hueDistanceThreshold = 30; // degrees apart in hue (on a 360° circle)

  const getHue = (hex: string): number => {
    const rgb = hexToRgb(hex);
    const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
    return hsl.h;
  };

  const hexToRgb = (hex: string) => {
    // Ensure hex is a valid string before calling replace
    if (!hex || typeof hex !== 'string') {
      return { r: 0, g: 0, b: 0 };
    }
    const cleaned = hex.replace(/^#/, '');
    const bigint = parseInt(cleaned, 16);
    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255,
    };
  };

  const rgbToHsl = (r: number, g: number, b: number) => {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b),
      min = Math.min(r, g, b);
    let h = 0,
      s = 0,
      l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }
      h *= 60;
    }

    return { h, s, l };
  };

  // Filter out invalid colors (null, undefined, empty strings)
  const validExistingColors = existingColors.filter(
    color => color && typeof color === 'string' && color.trim() !== ''
  );

  for (let i = 0; i < maxTries; i++) {
    const hue = Math.floor(Math.random() * 360);
    const color = `hsl(${hue}, 60%, 60%)`;
    const dummy = document.createElement('div');
    dummy.style.color = color;
    document.body.appendChild(dummy);
    const computed = getComputedStyle(dummy).color;
    document.body.removeChild(dummy);

    const match = computed.match(/\d+/g);
    if (!match) continue;
    const hex = `#${match.slice(0, 3).map(n => (+n).toString(16).padStart(2, '0')).join('')}`;

    const hueDiffers = validExistingColors.every(existingHex => {
      const existingHue = getHue(existingHex);
      return Math.abs(existingHue - hue) > hueDistanceThreshold;
    });

    if (hueDiffers) return hex;
  }

  // fallback
  return '#'+Math.floor(Math.random()*16777215).toString(16).padStart(6, '0');
}
